<!-- eslint-disable vue/attribute-hyphenation -->
<template>
  <AisInstantSearch
    :search-client="searchClient"
    :index-name="algoliaIndexName"
    :middlewares="middlewares"
    :routing="routing"
    class="site-search"
  >
    <div
      class="site-search__refinements"
      :class="activeFilter ? 'site-search__refinements--expanded' : ''"
    >
      <button
        class="filter-toggle__button filter-toggle__button--close"
        @click="toggleFilter"
      >
        <img
          src="/themes/custom/ncea/dist/images/icon-light-close.svg"
          alt="Close filter"
        />
      </button>
      <div class="breadcrumbs desktop">
        <div class="breadcrumbs__list-item">
          <a
            :href="isTeReo ? '/mi' : '/'"
            class="breadcrumbs__link-item"
          >
            <span class="text-tiny breadcrumbs__link-item-text">
              {{ trans('Home', !isTeReo) }}
            </span>
          </a>
          <span class="breadcrumbs__link-item-sep">/</span>
          <span class="text-tiny breadcrumbs__link-item-text">
            {{ trans('Search', !isTeReo) }}
          </span>
        </div>
      </div>
      <h1 class="heading-medium site-search__title">
        {{ trans('Search', !isTeReo) }}
      </h1>
      <AisClearRefinements />
      <h2 class="text-tiny site-search__refinement-list-title">
        {{ trans('Site area', !isTeReo) }}
      </h2>
      <AisRefinementList
        attribute="site_area"
        show-more
      />
      <h2 class="text-tiny site-search__refinement-list-title">
        {{ trans('Subject/Topic', !isTeReo) }}
      </h2>
      <AisRefinementList
        attribute="subject_topic"
        show-more
      >
        <template #showMoreLabel="{ isShowingMore }">
          <span v-if="isShowingMore">
            {{ trans('Show less', !isTeReo) }}
          </span>
          <span v-else>{{ trans('Show more', !isTeReo) }}</span>
        </template>
      </AisRefinementList>
      <h2 class="text-tiny site-search__refinement-list-title">
        {{ trans('Media type', !isTeReo) }}
      </h2>
      <AisRefinementList
        attribute="paragraph_type"
        :transform-items="transformParagraphTypes"
        show-more
      />
    </div>
    <div class="site-search__results education-container education-container--narrow">
      <div class="breadcrumbs tablet">
        <div class="breadcrumbs__list-item">
          <a
            :href="isTeReo ? '/mi' : '/'"
            class="breadcrumbs__link-item"
          >
            <span class="text-tiny breadcrumbs__link-item-text">
              {{ trans('Home', !isTeReo) }}
            </span>
          </a>
          <span class="breadcrumbs__link-item-sep">/</span>
          <span class="text-tiny breadcrumbs__link-item-text">
            {{ trans('Search', !isTeReo) }}
          </span>
        </div>
      </div>
      <h1 class="heading-medium site-search__title tablet">
        {{ trans('Search', !isTeReo) }}
      </h1>
      <div class="filter-toggle">
        <span class="filter-toggle__label">{{ trans('Filter by', !isTeReo) }}:</span>
        <button
          class="filter-toggle__button"
          @click="toggleFilter"
        >
          <img
            src="/themes/custom/ncea/dist/images/mobile-filter.svg"
            alt="Open filter"
          />
        </button>
      </div>
      <AisSearchBox
        ref="searchBox"
        :placeholder="trans('Search subjects, standards, activities and media', !isTeReo)"
      />
      <SiteSearchResults :isTeReo="isTeReo" />
    </div>
    <AisConfigure filters="published:true AND NOT is_orphan:true" />
  </AisInstantSearch>
</template>

<script>
import {
  AisInstantSearch,
  AisConfigure,
  AisSearchBox,
  AisRefinementList,
  AisClearRefinements,
} from 'vue-instantsearch/vue3/es';
import { history as historyRouter } from 'instantsearch.js/es/lib/routers';
import { singleIndex as singleIndexMapping } from 'instantsearch.js/es/lib/stateMappings';
import { createInsightsMiddleware } from 'instantsearch.js/es/middlewares';
import aa from 'search-insights';
import SiteSearchResults from './SiteSearchResults.vue';
import { searchClient, trans } from '../../utils/utils';

const insightsMiddleware = createInsightsMiddleware({
  insightsClient: aa,
  insightsInitParams: {
    useCookie: false,
  },
});
export default {
  name: 'SiteSearch',
  components: {
    AisInstantSearch,
    SiteSearchResults,
    AisRefinementList,
    AisConfigure,
    AisSearchBox,
    AisClearRefinements,
  },
  props: {
    algoliaIndexName: {
      type: String,
      required: true,
    },
    isTeReo: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      activeFilter: false,
      indexName: this.algoliaIndexName,
      middlewares: [insightsMiddleware],
      searchClient,
      routing: {
        router: historyRouter(),
        stateMapping: singleIndexMapping(this.algoliaIndexName),
      },
      query: '',
    };
  },
  methods: {
    /**
     * Used to toggle site search filter in mobile.
     */
    toggleFilter() {
      this.activeFilter = !this.activeFilter;
      document.body.classList.toggle('overflow-hidden-search');
    },
    transformParagraphTypes(items) {
      return items.map((item) => ({
        ...item,
        label: trans(item.label, !this.isTeReo),
      }));
    },
    trans,
  },
};
</script>

<style scoped lang="scss">
.site-search {
  display: flex;

  &__title {
    font-weight: 800;
  }

  &__refinement-list-title {
    font-weight: $font-weight-light !important;
    text-transform: uppercase;
    color: $color-charcoal;
  }

  .breadcrumbs {
    margin-bottom: 0;
  }

  &__refinements {
    display: flex;
    background-color: $color-white;
    align-self: flex-start;
    flex-direction: column;
    gap: 1rem;
    flex-basis: max(30rem, 20%);
    top: 0;
    height: 100vh;
    overflow-y: scroll;
    scrollbar-width: thin;
    position: sticky;
    padding: 2.5rem min(4vmin, 4rem);

    @include breakpoint-tablet-down {
      padding-block: 4.5rem;
    }

    @include education-breakpoint-tablet-down {
      display: none;
      padding-block: 2.5rem;
    }

    &--expanded {
      display: flex;
      flex-basis: 100%;
      position: fixed;
      z-index: 40;
      height: calc(100vh - 5rem);
      overflow: scroll;
    }
  }

  &__results {
    padding-block: 4.5rem;
  }

  .tablet {
    @include education-breakpoint-tablet-up {
      display: none;
    }
  }

  .desktop {
    @include education-breakpoint-tablet-down {
      display: none;
    }
  }
}

.filter-toggle {
  @include mobile-filter-toggle;

  padding-inline: 0;
  margin: 0 0 2rem 0;

  &__button {
    background: transparent;
    padding: 0;
    border: none;
    align-self: flex-end;
    margin-top: 2rem;

    img {
      vertical-align: middle;
    }

    &--close {
      @include education-breakpoint-tablet-up {
        display: none;
      }
    }
  }
}
</style>

<style lang="scss">
.site-search {
  .site-search-results__count {
    position: relative;
    top: 0;
    background: transparent;
  }

  .ais-SearchBox-form {
    display: grid;
  }

  .ais-SearchBox-input {
    @include searchbox-input;

    grid-area: 1/-1;
  }

  .ais-SearchBox-submit {
    display: none;
  }

  .ais-SearchBox-reset {
    display: inline-flex;
    border: none;
    grid-area: 1/-1;
    justify-self: flex-end;
    align-self: center;
    padding: 0 1.5rem;
    background: transparent;

    svg {
      width: 0.8rem;
      height: 0.8rem;
      padding: 0.1rem;
      fill: white;
      border-radius: 50%;
      background: $color-grey-s2;
    }
  }

  @include ais-refinement-list;
}
</style>
